import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_MENU_HEADER_ELEMENTS } from '../WidgetModel/impl';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import { ContainerChannelSlice } from 'src/services/WorkspaceService/Container/slices/ContainerChannelSlice';
import type { ContainerChannelSliceEventBus } from 'src/services/WorkspaceService/Container/slices/ContainerChannelSlice';
import { ContainerWidgetTabSlice } from 'src/services/WorkspaceService/Container/slices/ContainerWidgetTabSlice';
import type { ContainerWidgetTabSliceEventBus } from 'src/services/WorkspaceService/Container/slices/ContainerWidgetTabSlice';
import type { SelfOwnedWidget, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import { MergedEventBus } from 'src/types/EventBus/EventBus';
import type { EventBus, EventBusEvents, EventBusSignals } from 'src/types/EventBus/EventBus';

export type ErrorWidgetEventBus = EventBus<
    EventBusSignals<ContainerChannelSliceEventBus> & EventBusSignals<ContainerWidgetTabSliceEventBus>,
    EventBusEvents<ContainerChannelSliceEventBus> & EventBusEvents<ContainerWidgetTabSliceEventBus>
>;

export interface ErrorWidgetModelInit {
    container: WidgetHostContainer;
    tabId: WidgetId;
    widgetType: WidgetType;
    /** The construction failure this instance stands in for. */
    error: Error;
    /** The tab's persisted state blob, preserved verbatim so a failed widget never loses saved state. */
    persistedState: unknown;
    logger: Logger;
}

/**
 * Stand-in occupying a self-owned widget tab whose real model failed to
 * construct (ADR 0008). Holds the failure for display and the persisted state
 * for serialization round-trips, so a corrupt or transiently failing widget
 * never wipes saved state. The standard container slices keep the header's
 * tab/window chrome functional.
 */
export class ErrorWidgetModel implements SelfOwnedWidget<unknown>, Disposable {
    readonly #disposableStack = new DisposableStack();
    readonly #dirty = signal(false);
    readonly #persistedState: unknown;

    readonly widgetType: WidgetType;
    readonly error: Error;
    readonly eventBus: ErrorWidgetEventBus;
    readonly headerElements: WidgetHeaderElement[];

    constructor(init: ErrorWidgetModelInit) {
        this.widgetType = init.widgetType;
        this.error = init.error;
        this.#persistedState = init.persistedState;

        const channelSlice = this.#disposableStack.use(
            new ContainerChannelSlice({ container: init.container, tabId: init.tabId }),
        );
        const widgetTabSlice = this.#disposableStack.use(
            new ContainerWidgetTabSlice({ container: init.container, tabId: init.tabId, logger: init.logger }),
        );

        this.eventBus = MergedEventBus.from<ContainerChannelSliceEventBus>(
            channelSlice.eventBus,
        ).merge<ContainerWidgetTabSliceEventBus>(widgetTabSlice.eventBus);

        this.headerElements = COMMON_MENU_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#dirty;
    }

    get persistedState(): unknown {
        return this.#persistedState;
    }

    toJSON(): unknown {
        return this.#persistedState;
    }

    markClean(): void {
        // Nothing to clean: an errored stand-in never mutates its held state.
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
